import { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X, Pencil } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════
   Toast Notification System
   ═══════════════════════════════════════════════════════════════════ */
const ToastContext = createContext(null);

export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const idRef = useRef(0);

    const addToast = useCallback((message, type = 'info', duration = 4000) => {
        const id = ++idRef.current;
        setToasts(prev => [...prev, { id, message, type }]);
        if (duration > 0) {
            setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
        }
        return id;
    }, []);

    const api = useRef(null);
    if (!api.current) {
        api.current = {
            success: (msg) => addToast(msg, 'success'),
            error: (msg) => addToast(msg, 'error'),
            info: (msg) => addToast(msg, 'info'),
            warning: (msg) => addToast(msg, 'warning'),
        };
    }

    const icons = {
        success: <CheckCircle className="w-5 h-5 text-emerald-600" />,
        error: <XCircle className="w-5 h-5 text-red-600" />,
        info: <Info className="w-5 h-5 text-blue-600" />,
        warning: <AlertTriangle className="w-5 h-5 text-amber-600" />,
    };
    const colors = {
        success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
        error: 'bg-red-50 border-red-200 text-red-800',
        info: 'bg-blue-50 border-blue-200 text-blue-800',
        warning: 'bg-amber-50 border-amber-200 text-amber-800',
    };

    return (
        <ToastContext.Provider value={api.current}>
            {children}
            <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none" style={{ maxWidth: 400 }}>
                {toasts.map(t => (
                    <div
                        key={t.id}
                        className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg backdrop-blur-sm ${colors[t.type]}`}
                        style={{ animation: 'toastSlideIn 0.3s ease-out' }}
                    >
                        <span className="flex-shrink-0 mt-0.5">{icons[t.type]}</span>
                        <p className="text-sm font-medium flex-1">{t.message}</p>
                        <button
                            onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
                            className="text-current opacity-40 hover:opacity-70 text-lg leading-none flex-shrink-0"
                        >×</button>
                    </div>
                ))}
            </div>
            <style>{`
                @keyframes toastSlideIn {
                    from { opacity: 0; transform: translateX(40px) scale(0.95); }
                    to { opacity: 1; transform: translateX(0) scale(1); }
                }
                @keyframes modalScaleIn {
                    from { opacity: 0; transform: scale(0.92) translateY(10px); }
                    to { opacity: 1; transform: scale(1) translateY(0); }
                }
            `}</style>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast must be inside ToastProvider');
    return ctx;
}


/* ═══════════════════════════════════════════════════════════════════
   Modal Overlay
   ═══════════════════════════════════════════════════════════════════ */
export default function Modal({ isOpen, onClose, title, children, maxWidth = 'max-w-md' }) {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            return () => { document.body.style.overflow = ''; };
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <div
                className={`relative bg-white rounded-2xl shadow-2xl w-full ${maxWidth} overflow-hidden`}
                style={{ animation: 'modalScaleIn 0.25s ease-out' }}
            >
                {title && (
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
                        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
                {children}
            </div>
        </div>
    );
}


/* ═══════════════════════════════════════════════════════════════════
   ConfirmDialog — beautiful replacement for confirm()
   ═══════════════════════════════════════════════════════════════════ */
export function ConfirmDialog({
    open, onClose, onConfirm,
    title = "Tasdiqlash",
    message = "Davom etishni xohlaysizmi?",
    icon = null,
    confirmText = "Ha, davom etish",
    cancelText = "Bekor qilish",
    danger = false,
}) {
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await onConfirm();
        } finally {
            setLoading(false);
            onClose();
        }
    };

    const defaultIcon = danger
        ? <AlertTriangle className="w-7 h-7 text-red-500" />
        : <AlertTriangle className="w-7 h-7 text-amber-500" />;

    return (
        <Modal isOpen={open} onClose={loading ? undefined : onClose} maxWidth="max-w-sm">
            <div className="p-6 text-center">
                <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center
                    ${danger ? 'bg-red-50' : 'bg-amber-50'}`}>
                    {icon || defaultIcon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
                <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto leading-relaxed">{message}</p>
                <div className="flex gap-3 justify-center">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading}
                        className={`px-5 py-2.5 text-sm font-medium text-white rounded-xl transition disabled:opacity-50
                            ${danger ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            </span>
                        ) : confirmText}
                    </button>
                </div>
            </div>
        </Modal>
    );
}


/* ═══════════════════════════════════════════════════════════════════
   PromptDialog — beautiful replacement for prompt()
   ═══════════════════════════════════════════════════════════════════ */
export function PromptDialog({
    open, onClose, onSubmit,
    title = "Ma'lumot kiriting",
    label = "",
    placeholder = "",
    defaultValue = "",
    icon = null,
    submitText = "Saqlash",
    cancelText = "Bekor",
    description = "",
}) {
    const [value, setValue] = useState(defaultValue);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setValue(defaultValue);
            setTimeout(() => {
                inputRef.current?.focus();
                inputRef.current?.select();
            }, 120);
        }
    }, [open, defaultValue]);

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (!value.trim()) return;
        setLoading(true);
        try {
            await onSubmit(value.trim());
        } finally {
            setLoading(false);
            onClose();
        }
    };

    return (
        <Modal isOpen={open} onClose={loading ? undefined : onClose}>
            <form onSubmit={handleSubmit}>
                <div className="p-6">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center flex-shrink-0">
                            {icon || <Pencil className="w-5 h-5 text-blue-600" />}
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 leading-tight">{title}</h3>
                            {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
                        </div>
                    </div>
                    {label && <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>}
                    <input
                        ref={inputRef}
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        placeholder={placeholder}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 text-sm transition"
                        autoComplete="off"
                    />
                </div>
                <div className="flex justify-end gap-2 px-6 py-4 bg-gray-50/80 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2.5 text-sm font-medium text-gray-600 rounded-xl hover:bg-gray-200 transition disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        type="submit"
                        disabled={loading || !value.trim()}
                        className="px-6 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition disabled:opacity-50"
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Saqlanmoqda...
                            </span>
                        ) : submitText}
                    </button>
                </div>
            </form>
        </Modal>
    );
}
